export enum VoucherType {
  Delivery = "delivery",
  Value = "value",
  Percentage = "percentage",
}

/** Bairro escolhido para um endereço salvo em UMA loja (id do bairro da loja). */
export interface IAddressDistrict {
  id: number;
  name: string;
}

/**
 * Endereço salvo no navegador do cliente (localStorage "__addss"), compartilhado
 * por TODAS as lojas MenuChef do domínio. Rua, número, cidade, CEP etc. valem em
 * qualquer loja; o bairro que define a taxa não: cada loja tem os seus bairros
 * (ids próprios). Por isso o id do bairro fica por loja em `districtByStore`, e
 * `district` é só o nome (o último escolhido/digitado), sem id — vale como dica e
 * como texto do pedido. Helpers e migração do formato antigo (um `districtId`
 * único): src/pages/Payments/helpers/savedAddresses.ts.
 */
export interface IAddress {
  key: string;
  address: string;
  number: string;
  /** Nome do bairro, sem vínculo com loja (último escolhido/digitado). */
  district: string;
  /** Bairro escolhido em cada loja: id da loja -> bairro daquela loja. */
  districtByStore?: { [storeId: string]: IAddressDistrict };
  cityId: number;
  city: string;
  zipCode: string;
  complement: string;
  reference: string;
}

export interface IExtra {
  id: string;
  name: string;
  price: number;
  amount: number;
  max: number;
  stepId?: any;
  stepDescription?: string;
}

export interface ISize {
  id: string;
  name: string;
  initials: string;
  fractioned: boolean;
  fractionalMaximum: number;
  selected: number;
  total: number;
  obs: string;
  groupId: string;
  groupName: string;
}

/** "all"/ausente = aparece em todos os canais (padrão). "menu" = só no cardápio-vitrine. "store" = só na jornada de compra. */
export type ProductVisibility = "all" | "menu" | "store";

export interface IProduct {
  id: string;
  name: string;
  description: string;
  price: number;
  promotionalPrice: number;
  imageUrl: string;
  obs?: string;
  amount?: number;
  step: boolean;
  extras: IExtra[];
  total?: number;
  totalExtra?: number;
  isUnitKg: boolean;
  visibility?: ProductVisibility | null;
}

export interface IStepOption {
  id: string;
  name: string;
  price: number;
  maximum: number;
  amount: number;
  imageUrl: string;
}

export interface IStep {
  id: string;
  name: string;
  surname: string;
  minimum: number;
  maximum: number;
  selected: number;
  error: boolean;
  options: IStepOption[];
  type: string;
}

export interface ICartProduct {
  id: string;
  product_id: string;
  product: string;
  price: number;
  grouping_id: number;
  sizeId?: any;
  obs: string;
  imageUrl: string;
  amount: number;
  extras: IExtra[];
  totalProduct: number;
  totalExtra: number;
  total: number;
  flavours?: ICartProduct[];
}

export interface ICart {
  grouping_last: number;
  product_last: number;
  products: ICartProduct[];
  payments: IPayment[];
  total: number;
  /**
   * Configuração de venda da loja (`sale` | `share` | `menu`), vinda do backend.
   * Decide qual botão de ação aparece no pagamento. NÃO confundir com `channel`.
   */
  styleSale: string;
  /**
   * Rota pela qual o cliente entrou: `/store` ou `/menu`. Decide a navegação da
   * sacola e se a venda pode ser fechada (`/menu` é cardápio-vitrine, nunca fecha).
   * Ausente em carrinhos gravados por versões antigas — use `getCartChannel`.
   */
  channel?: "store" | "menu";
}

export interface ICity {
  id: number;
  name: string;
  state: string;
  zipCode: string;
  number: string;
}

export interface IDistance {
  id: number;
  externalId: string;
  name: string;
  initial: number;
  final: number;
  value: number;
}

export interface IDistrict {
  id: number;
  externalId: string;
  name: string;
  tax: number;
}

export interface IStoreTheme {
  /** Cara da loja: header, abas, botões, links. */
  primary: string;
  /** Preços, selos positivos, sucesso. */
  secondary: string;
  /** Badge de contagem do carrinho, detalhes. */
  accent: string;
  /** Fundo da página (faixa clara — normalizado no frontend). */
  background: string;
}

export interface IStore {
  id: string;
  name: string;
  status: string;
  suffix?: string;
  address: string;
  number: string;
  district: string;
  zipCode: string;
  phone: string;
  city: ICity;
  imageUrl: string;
  tax: number;
  fractionalPrice: string;
  distances: IDistance[];
  districts: IDistrict[];
  initialDeliveryTime: number;
  finalDeliveryTime: number;
  cellPhone: string;
  minimumSaleValue: number;
  hasDelivery: boolean;
  hasPickup: boolean;
  freeShippingFrom: number;

  isVoucher: boolean;
  /** Configuração de venda da loja: `sale` | `share` | `menu`. */
  styleSale?: string;
  /** Tema escolhido pela loja. Ausente/null = tema padrão MenuChef. */
  theme?: IStoreTheme | null;
  /** Imagem de capa (faixa do topo). Ausente/null = a capa usa imageUrl (comportamento atual). */
  coverImageUrl?: string | null;
}

export interface IGroup {
  id: string;
  name: string;
  products: IProduct[];
  sizes?: ISize[];
  /** Ícone escolhido pelo dono da loja. Ausente/null = deduzir pelo nome. */
  icon?: string | null;
  /** Nomes dos sabores/produtos vendidos por tamanho — usados só na busca. */
  flavorNames?: string[];
}

export interface IPayment {
  id: string;
  name: string;
  money: boolean;
  value: number;
  changeFor: number;
}

export interface ISaleRelationId {
  id: string;
  // api
  name?: string;
  imageUrl?: string;
  address?: string;
  number?: string;
  district?: string;
  zipCode?: string;
  phone?: string;
  cellPhone?: string;
  city?: ISaleRelationIdNumber;
  suffix?: string;
}

export interface ISaleRelationIdNumber {
  id: number;
  // api
  name?: string;
  state?: string;
}

export interface ISaleProductFlavourExtra {
  extra: ISaleRelationId;
  amount: number;
  price: number;
}

export interface ISaleProductFlavour {
  product: ISaleRelationId;
  amount: number;
  price: number;
  discount: number;
  addition: number;
  note: string;
  extras: ISaleProductFlavourExtra[];
}

export interface ISaleProductExtra {
  extra: ISaleRelationId;
  amount: number;
  price: number;
  step?: ISaleRelationId;
}

export interface ISaleProduct {
  product?: ISaleRelationId;
  size?: ISaleRelationId;
  amount: number;
  price: number;
  discount: number;
  addition: number;
  note: string;
  extras: ISaleProductExtra[];
  flavours: ISaleProductFlavour[];
  // api
  total?: number;
  imageUrl?: string;
  name?: string;
}

export interface ISalePayment {
  payment: ISaleRelationId;
  value: number;
  changeFor: number;
  name?: string;
}

export interface ISaleStatus {
  status: string;
  createdAt: string;
}

export interface ISale {
  store: ISaleRelationId;
  document?: number;
  taxPayerIdentificationNumber: string;
  note: string;
  phone: string;
  deliveryType: string;
  deliveryName: string;
  deliveryAddress: string;
  deliveryNumber: string;
  deliveryDistrict: string;
  deliveryComplement: string;
  deliveryReference: string;
  deliveryZipCode: string;
  deliveryCity?: ISaleRelationIdNumber;
  products: ISaleProduct[];
  payments: ISalePayment[];
  tax: number;
  // api
  total?: number;
  status?: string;
  updateStatus?: string;
  statuses?: ISaleStatus[];
  reasonCancellation?: string;
  voucher?: { id: string } | null;
  voucherCode?: string | null;
  voucherValue: number | null;
  voucherType: VoucherType | null;
}

export interface IUser {
  name: string;
  phone: string;
}

export interface ISaleForm {
  taxPayerIdentificationNumber: string,
  name: string,
  phone: string,
  zipCode: string,
  address: string,
  number: string,
  district: string,
  districtId?: number | null,
  cityId: number,
  city: string,
  complement: string,
  reference: string,
  deliveryType: "delivery" | "pickUp",
  changeFor: string,
  deliveryFee: number,
  voucherId?: string | null,
  voucherCode?: string | null,
  voucherValue: number | null,
  voucherType: VoucherType | null
}

// ---- Painel admin da loja (v1 somente consulta) ----

export type AdminSaleStatus =
  | "waiting"
  | "integrated"
  | "canceled"
  | "denied"
  | "confirmed"
  | "delivering"
  | "ready"
  | "concluded";

export type AdminDeliveryType = "delivery" | "pickUp" | "table";

export interface IAdminSale {
  id: string;
  document: number;
  deliveryType: AdminDeliveryType;
  deliveryName: string;
  phone: string;
  tax: number;
  total: number;
  status: AdminSaleStatus;
  createdAt: string;
}

export interface IAdminGroup {
  id: string;
  externalId: string;
  name: string;
  active: boolean;
  order?: number | null;
  createdAt: string;
  /** Ícone escolhido pelo dono da loja. Ausente/null = deduzir pelo nome. */
  icon?: string | null;
}

export interface IAdminExtra {
  id: string;
  name: string;
  price: number;
  active: boolean;
}

export interface IAdminCatalogProduct {
  id: string;
  active: boolean;
  externalId: string;
  name: string;
  description: string;
  price: number;
  imageUrl: string | null;
  step: boolean;
  visibility?: ProductVisibility | null;
}

export interface IAdminCatalogSize {
  id: string;
  sizeId: string;
  externalId: string;
  name: string;
  initials: string;
  fractioned: boolean;
  fractionalMaximum: number;
}

export interface IAdminCatalogGroup {
  id: string;
  active: boolean;
  name: string;
  products: IAdminCatalogProduct[];
  sizes: IAdminCatalogSize[];
}

export interface IAdminStore extends IStore {
  active?: boolean;
}